import { proxy, subscribe, useSnapshot } from 'valtio'

import { updateUserUiDataSettings } from '@/features/domain/ui'
import { selectSchedulerPreferences } from '@/features/domain/user'
import { isDeepEqual } from '@/server-data'
import { domainProxy, store } from '@/store'
import { unproxify } from '@/utils'

export interface SchedulerPreferences {
  showUnavailableRoutes: boolean
  showApprovedRoutes: boolean
  showAbsoluteValues: boolean
  groupBy: 'dateAsString' | 'vehicleId'
  sorters: {
    field: string
    ascending: boolean
  }[]
}

type SchedulerPreferencesAtom = { preferences: SchedulerPreferences }

// Default values
// ------------------------------------
const defaultSorters = [
  {
    field: 'vehicleAvatar',
    ascending: true,
  },
]
const defaultSchedulerPreferences: SchedulerPreferences = {
  groupBy: 'dateAsString',
  sorters: defaultSorters,
  showAbsoluteValues: false,
  showApprovedRoutes: false,
  showUnavailableRoutes: false,
}

const createDefaultSchedulerPreferences = (): SchedulerPreferencesAtom => ({
  preferences: selectSchedulerPreferences(store.getState()) || defaultSchedulerPreferences,
})

export const schedulerPreferencesAtom = proxy<SchedulerPreferencesAtom>(
  createDefaultSchedulerPreferences(),
)

domainProxy.subscribeToDataChangeSuccess(domainAction => {
  // If the domain action is fromSelf, it means that the action was triggered by the user
  // and we don't want to update the routing layout again
  if (domainAction.mode === 'update' && domainAction.fromSelf) return

  const newPreferences = selectSchedulerPreferences(store.getState())
  if (isDeepEqual(newPreferences, unproxify(schedulerPreferencesAtom.preferences))) return
  //console.log('new preferences', newPreferences)
  setSchedulerPreferences(newPreferences)
})

subscribe(schedulerPreferencesAtom.preferences, () => {
  const storePreferences = selectSchedulerPreferences(store.getState())
  if (isDeepEqual(storePreferences, unproxify(schedulerPreferencesAtom.preferences))) return

  //console.log('updateing server-data preferences', schedulerPreferencesAtom.preferences)
  store.dispatch(
    updateUserUiDataSettings({
      schedulerPreferences: unproxify(schedulerPreferencesAtom.preferences),
    }),
  )
})

// ------------------------------------
// Write functions
// ------------------------------------

type SetSchedulerPreferences = (prev: SchedulerPreferences) => SchedulerPreferences
export type SchedulerPreferencesSetter = SetSchedulerPreferences | Partial<SchedulerPreferences>

export const setSchedulerPreferences = (valueOrFunc: SchedulerPreferencesSetter) => {
  // callback with prev value
  if (typeof valueOrFunc === 'function') {
    Object.assign(
      schedulerPreferencesAtom.preferences,
      valueOrFunc(schedulerPreferencesAtom.preferences),
    )
  } else {
    // atomic update
    for (const field of Object.keys(valueOrFunc)) {
      schedulerPreferencesAtom.preferences[field] = valueOrFunc[field]
    }
  }

  return schedulerPreferencesAtom.preferences
}

// ------------------------------------
// React Hooks
// ------------------------------------

export const useSchedulerPreferencesAtom = () => {
  return [useSnapshot(schedulerPreferencesAtom).preferences, setSchedulerPreferences] as const
}
